<template>
  <div class="appearance-settings" @keydown.up.stop @keydown.down.stop @keydown.left.stop @keydown.right.stop>
    <!-- The preview follows every setting — the table style too, on the page that offers it:
         rounded toggles for « Classique », square cells for « Grille » (data-style). -->
    <div class="appearance-preview" :data-density="preferences.density" :data-text-size="preferences.textSize"
      :data-style="showStyles ? preferences.style : undefined"
      :class="{ 'appearance-preview--details': !preferences.hideStudentDetails }">
      <div class="preview-caption">
        <span>Aperçu<template v-if="showStyles"> · {{ styleTitle }}</template></span>
        <span class="preview-live"><span /> En direct</span>
      </div>
      <!-- The wall is not a table, so the table mock cannot stand for it: it draws territories
           and one tile per student, and it answers the wall's OWN settings (#814). It reuses
           `wallName` and `wallComparer`, so the names it shows cannot drift from the real ones. -->
      <svg v-if="isWallPreview" viewBox="0 0 440 132" role="img"
        aria-label="Aperçu du mur avec vos réglages d’affichage">
        <template v-if="preferences.wallGrouping !== 'none'">
          <path v-for="(d, i) in PREVIEW_TERRITORIES" :key="i" :d="d" class="preview-wall-territory" />
        </template>
        <g v-for="(tile, i) in previewWallTiles" :key="i" class="preview-wall-tile"
          :class="{ 'preview-wall-tile--recorded': tile.recorded }">
          <rect :x="tile.x" :y="tile.y" width="100" height="58" rx="7" />
          <!-- The territory names itself once, on its first tile — as the wall does. With no
               territory at all there is no first tile to carry it, so every tile does. -->
          <text v-if="tile.label" class="preview-wall-major" :x="tile.x + 8" :y="tile.y + 14">{{ tile.label }}</text>
          <text class="preview-wall-name" :x="tile.x + 8" :y="tile.y + 34">{{ tile.name }}</text>
          <rect v-for="d in 5" :key="d" class="preview-wall-strip"
            :class="{ 'preview-wall-strip--on': tile.recorded && d <= 3 }"
            :x="tile.x + 8 + (d - 1) * 15" :y="tile.y + 45" width="12" height="4" rx="2" />
        </g>
      </svg>
      <svg v-else viewBox="0 0 440 132" role="img" aria-label="Aperçu du tableau avec vos réglages d’affichage">
        <g class="preview-header"><rect x="0" y="0" width="440" height="26" rx="5" />
          <text x="12" y="17">Élève</text><text v-for="(day, i) in ['Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.']" :key="day" :x="162 + i * 58" y="17">{{ day }}</text>
        </g>
        <g v-for="(name, i) in ['Camille Roy', 'Alex Martin', 'Charlie Gagnon', 'Sam Dubois']" :key="name" :class="['preview-row', `preview-row-${i}`]">
          <rect class="preview-stripe" x="0" y="0" width="440" />
          <text class="preview-name" x="12" y="15">{{ name }}</text>
          <text class="preview-detail" x="12" y="29">Programme · Tuteur</text>
          <g v-for="day in 5" :key="day" class="preview-cell">
            <rect :x="142 + (day - 1) * 58" y="3" width="53" rx="3" />
            <text :x="168 + (day - 1) * 58" y="15">{{ i === 2 && day === 3 ? '04' : '02' }}</text>
          </g>
        </g>
      </svg>
    </div>

    <!-- Every setting here belongs to THIS page (decided on the 2026-09-09 call: the view one
         wants compact is not the one one wants comfortable). The footer says so, with the page's
         name; the table style is simply one more setting, on the one page that offers it. -->
    <fieldset class="appearance-fieldset">
      <legend>Espace et taille des cellules</legend>
      <div class="density-options">
        <label v-for="option in TEACHING_GRID_DENSITIES" :key="option.value" class="density-option"
          :class="{ 'density-option--selected': preferences.density === option.value }">
          <input type="radio" :name="densityGroupName" :value="option.value" :checked="preferences.density === option.value"
            @change="apply(preferences.setDensity(option.value))" />
          <span class="density-miniature" :data-density="option.value" aria-hidden="true"><span v-for="row in 4" :key="row"><i /><i /><i /><i /></span></span>
          <span class="density-label">{{ option.title }}<v-icon v-if="preferences.density === option.value" size="12">fa-check</v-icon></span>
          <span class="density-description">{{ descriptions[option.value] }}</span>
        </label>
      </div>
      <!-- Said rather than hidden: the setting is remembered for « Classique » and « Grille »,
           so removing it under « Mur » would look like it had been lost. -->
      <p v-if="isWallPreview" class="appearance-note appearance-note--under">
        Le mur garde des cases de taille fixe : l’espacement et la taille du texte s’appliquent
        aux styles « Classique » et « Grille ».
      </p>
    </fieldset>

    <fieldset v-if="showStyles" class="appearance-fieldset">
      <legend>Style du tableau</legend>
      <div class="style-options">
        <label v-for="option in TEACHING_GRID_STYLES" :key="option.value" class="density-option"
          :class="{ 'density-option--selected': preferences.style === option.value }">
          <input type="radio" :name="styleGroupName" :value="option.value" :checked="preferences.style === option.value"
            @change="apply(preferences.setStyle(option.value))" />
          <span class="style-miniature" :data-style="option.value" aria-hidden="true"><span v-for="row in 4" :key="row"><i /><i /><i /><i /></span></span>
          <span class="density-label">{{ option.title }}<v-icon v-if="preferences.style === option.value" size="12">fa-check</v-icon></span>
          <span class="density-description">{{ styleDescriptions[option.value] }}</span>
        </label>
      </div>
    </fieldset>

    <!-- The wall's only two settings (#814). Its geometry stays frozen at the values the
         measurements validate — a band height of 2 would split groups without saying so. -->
    <template v-if="showStyles && preferences.isWallStyle">
      <!-- A select rather than a toggle row: four axes with labels this long do not fit four
           buttons across the panel. « État et programme » recomposes the wall as presences are
           recorded, which is useful to finish a day and disconcerting otherwise — hence the
           note under it. -->
      <div class="appearance-setting-row">
        <div><div class="setting-label">Regrouper par</div><p>Ce que le mur encadre.</p></div>
        <v-select :model-value="preferences.wallGrouping" :items="WALL_GROUPINGS" item-value="value"
          item-title="title" hide-details class="appearance-select" aria-label="Regroupement du mur"
          @update:model-value="apply(preferences.setWallGrouping($event))" />
      </div>
      <p v-if="preferences.wallGrouping === 'status'" class="appearance-note">
        Une case change d'encadré dès qu'elle est saisie : le mur se recompose pendant la saisie.
      </p>
      <div class="appearance-setting-row">
        <div><div class="setting-label">Trier par</div><p>Ordonne les élèves dans leur groupe.</p></div>
        <v-btn-toggle :model-value="preferences.wallSort" mandatory divided color="primary" variant="outlined"
          aria-label="Tri du mur" @update:model-value="apply(preferences.setWallSort($event))">
          <v-btn v-for="option in WALL_SORTS" :key="option.value" :value="option.value" size="small">
            {{ option.title }}
          </v-btn>
        </v-btn-toggle>
      </div>
      <div class="appearance-setting-row">
        <div><div class="setting-label">Nom affiché</div><p>Ce que porte chaque case.</p></div>
        <v-btn-toggle :model-value="preferences.wallNameFormat" mandatory divided color="primary" variant="outlined"
          aria-label="Nom affiché dans une case"
          @update:model-value="apply(preferences.setWallNameFormat($event))">
          <v-btn v-for="option in WALL_NAME_FORMATS" :key="option.value" :value="option.value" size="small">
            {{ option.title }}
          </v-btn>
        </v-btn-toggle>
      </div>
    </template>

    <div class="appearance-setting-row">
      <div><div class="setting-label">Taille du texte</div><p>Affinez la lisibilité.</p></div>
      <v-btn-toggle :model-value="preferences.textSize" mandatory divided color="primary" variant="outlined"
        aria-label="Taille du texte" @update:model-value="apply(preferences.setTextSize($event))">
        <v-btn value="auto" size="small" aria-label="Taille automatique">Auto</v-btn>
        <v-btn value="small" size="small" aria-label="Petit texte"><span class="sample-small">A</span></v-btn>
        <v-btn value="normal" size="small" aria-label="Texte normal"><span class="sample-normal">A</span></v-btn>
        <v-btn value="large" size="small" aria-label="Grand texte"><span class="sample-large">A</span></v-btn>
      </v-btn-toggle>
    </div>
    <div class="appearance-setting-row">
      <div><div class="setting-label">Détails des élèves</div><p>Programme, tuteur et identifiants.</p></div>
      <v-btn-toggle :model-value="preferences.details" mandatory divided color="primary" variant="outlined"
        aria-label="Détails des élèves" @update:model-value="apply(preferences.setDetails($event))">
        <v-btn value="auto" size="small">Auto</v-btn><v-btn value="show" size="small">Afficher</v-btn><v-btn value="hide" size="small">Masquer</v-btn>
      </v-btn-toggle>
    </div>
    <p class="appearance-note">Les détails masqués restent accessibles au survol ou au clavier.</p>
    <div class="appearance-footer">
      <span><v-icon size="12" aria-hidden="true">fa-thumbtack</v-icon> Réglages propres à la page « {{ pageTitle }} »</span>
      <v-btn variant="text" size="small" prepend-icon="fa-arrow-rotate-left" @click="apply(preferences.reset())">Réinitialiser</v-btn>
    </div>
    <p v-if="storageFailed" class="text-error text-caption" role="alert">Réglages appliqués. Ce navigateur ne permet pas de les mémoriser.</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue';
import { useRoute } from 'vue-router';
import { TEACHING_GRID_DENSITIES, TEACHING_GRID_STYLES, useTeachingGridDensity } from '@/stores/teaching-grid-density';
import {
  WALL_GROUPINGS, WALL_NAME_FORMATS, WALL_SORTS, wallComparer, wallName,
} from '@/views/teaching/presence-wall-model';
import type { StudentRow } from '@/views/teaching/presence-sheet-row';

const preferences = useTeachingGridDensity();
// The panel is teleported by v-menu / BaseDialog but keeps the app context, so the router
// is reachable. The table style is a per-page choice: only the Présences route declares it
// (routes.ts), the three other teaching matrices have one rendering.
const route = useRoute();
const showStyles = computed(() => route.meta.teachingGridStyles === true);
const pageTitle = computed(() => String(route.meta.title ?? ''));
const styleTitle = computed(() => TEACHING_GRID_STYLES.find(option => option.value === preferences.style)?.title ?? '');
const densityGroupName = useId();
const styleGroupName = useId();
const storageFailed = ref(false);
const descriptions = { compact: 'Vue d’ensemble', normal: 'Équilibré', comfortable: 'Plus d’espace' };
const styleDescriptions = {
  classic: 'Feuille habituelle',
  grid: 'Grille dense, en-tête fixe',
  // Short enough to sit in a third of the panel without wrapping to three lines.
  wall: 'Une case par élève, par groupe',
};
function apply(saved: boolean) { storageFailed.value = !saved; }

// ── The wall preview (#814) ───────────────────────────────────────────────────────────────
// The table mock says nothing true about the wall: no rows, no day columns, one tile per
// student inside an outlined territory. What follows draws that instead — eight tiles on two
// rows of four, the second territory wrapping across both, which is the shape `packWall` really
// produces (a strip filled in reading order, not a rectangle).
//
// Geometry is fixed on purpose: a wall cell is 124 × 74 whatever the density, and its name is
// 11.5px whatever the text size (CELL_W / CELL_H / .cell-name in presence-wall-grid.vue). The
// preview would lie if it breathed with those settings. See the note under the density fieldset.
const isWallPreview = computed(() => showStyles.value && preferences.isWallStyle);

/** The two territory outlines: columns 0-1 of row 0, then the L that wraps onto row 1. */
const PREVIEW_TERRITORIES = [
  'M8 1 H218 V63 H8 Z',
  'M220 1 H430 V127 H8 V65 H220 Z',
];

/** Minimal rows: `wallName` and `wallComparer` only read the student's user. */
const PREVIEW_WALL_STUDENTS = [
  ['Camille', 'Roy'], ['Alex', 'Martin'], ['Charlie', 'Gagnon'], ['Sam', 'Dubois'],
  ['Noa', 'Tremblay'], ['Jade', 'Fortin'], ['Maxime', 'Côté'], ['Théo', 'Ouellet'],
].map(([firstName, lastName]) => ({
  frequentation: { student: { user: { firstName, lastName } } },
} as unknown as StudentRow));

/** What each territory calls itself, on the axis chosen. Empty on « Aucun » — see the template. */
const previewMajorLabels = computed<[string, string]>(() => {
  switch (preferences.wallGrouping) {
    case 'status': return ['À saisir', 'Présent'];
    case 'competency': return ['Affûtage · 02', 'Affûtage · 04'];
    case 'none': return ['Affûtage', 'Affûtage'];
    default: return ['Affûtage', 'Charpenterie'];
  }
});

const previewWallTiles = computed(() => {
  const names = [...PREVIEW_WALL_STUDENTS]
    .sort(wallComparer(preferences.wallSort))
    .map(row => wallName(row, preferences.wallNameFormat));
  const [firstLabel, secondLabel] = previewMajorLabels.value;
  const noTerritories = preferences.wallGrouping === 'none';
  return names.map((name, i) => ({
    name,
    x: 10 + (i % 4) * 106,
    y: 3 + Math.floor(i / 4) * 64,
    // The first two tiles are the ones left to record, which is also what « État et programme »
    // puts in its first territory — the two readings agree.
    recorded: i >= 2,
    label: noTerritories ? firstLabel : (i === 0 ? firstLabel : (i === 2 ? secondLabel : '')),
  }));
});
</script>

<style scoped>
.appearance-settings { display: flex; flex-direction: column; gap: 14px; }
.appearance-preview { --preview-step: 34px; --preview-font: 12px; border: 1px solid rgba(var(--v-theme-primary), 0.14); border-radius: 10px; padding: 10px; background: rgba(var(--v-theme-primary), 0.025); overflow: hidden; }
.appearance-preview[data-density="compact"] { --preview-step: 23px; --preview-font: 10px; }
.appearance-preview[data-density="comfortable"] { --preview-step: 43px; --preview-font: 14px; }
.appearance-preview--details[data-density="compact"] { --preview-step: 34px; }
.appearance-preview[data-text-size="small"] { --preview-font: 10px; }
.appearance-preview[data-text-size="normal"] { --preview-font: 12px; }
.appearance-preview[data-text-size="large"] { --preview-font: 14px; }
.preview-caption { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 11px; font-weight: 600; color: rgba(var(--v-theme-on-surface), 0.7); }
.preview-live { display: flex; align-items: center; gap: 5px; color: rgb(var(--v-theme-primary)); }
.preview-live > span { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }
.appearance-preview svg { width: 100%; display: block; height: 128px; }
.preview-header rect { fill: rgb(var(--v-theme-primary)); }
.preview-header text { fill: rgb(var(--v-theme-on-primary)); font-size: 10px; font-weight: 600; }
.preview-row { transform: translateY(calc(28px + var(--preview-index) * var(--preview-step))); transition: transform 260ms ease; }
.preview-row-0 { --preview-index: 0; }.preview-row-1 { --preview-index: 1; }.preview-row-2 { --preview-index: 2; }.preview-row-3 { --preview-index: 3; }
.preview-stripe { height: calc(var(--preview-step) - 2px); fill: rgba(var(--v-theme-primary), 0.035); transition: height 260ms ease; }
.preview-name, .preview-cell text { fill: rgb(var(--v-theme-on-surface)); font-size: var(--preview-font); transition: font-size 260ms ease; }
.preview-detail { fill: rgba(var(--v-theme-on-surface), 0.65); font-size: 9px; opacity: 0; transition: opacity 160ms ease; }
.appearance-preview--details .preview-detail { opacity: 1; }
.preview-cell rect { height: calc(var(--preview-step) - 7px); fill: rgba(var(--v-theme-primary), 0.09); transition: height 260ms ease, rx 260ms ease; }
.preview-cell text { text-anchor: middle; }
/* The table style, where the page offers one: « Classique » keeps the sheet's rounded toggles,
   « Grille » squares its cells (rx is a CSS geometry property on SVG rects). */
.appearance-preview[data-style="classic"] .preview-cell rect { rx: 8px; }
.appearance-preview[data-style="grid"] .preview-cell rect { rx: 1.5px; }

/* The wall preview. Its tiles do NOT follow --preview-step: a wall cell has a fixed size
   whatever the density, and the preview says only what is true. */
.preview-wall-territory { fill: none; stroke: rgba(var(--v-theme-primary), 0.45); stroke-width: 1.5; stroke-dasharray: 3 2; }
.preview-wall-tile rect:first-of-type { fill: rgba(var(--v-theme-primary), 0.07); }
.preview-wall-tile--recorded rect:first-of-type { fill: rgba(var(--v-theme-primary), 0.2); }
.preview-wall-major { fill: rgba(var(--v-theme-primary), 0.75); font-size: 7.5px; font-weight: 800; letter-spacing: 0.04em; }
/* Fixed, like the wall's own `.cell-name`: the text-size setting does not reach the wall. */
.preview-wall-name { fill: rgb(var(--v-theme-on-surface)); font-size: 11px; font-weight: 600; }
.preview-wall-strip { fill: rgba(var(--v-theme-on-surface), 0.14); }
.preview-wall-strip--on { fill: rgba(var(--v-theme-primary), 0.65); }
.appearance-fieldset { border: 0; min-width: 0; }
.appearance-fieldset legend, .setting-label { font-size: 13px; font-weight: 600; }
.appearance-fieldset legend { margin-bottom: 10px; }
.density-options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.density-option { position: relative; display: flex; flex-direction: column; padding: 10px; border: 1px solid rgba(var(--v-theme-primary), 0.18); border-radius: 9px; cursor: pointer; background: rgb(var(--v-theme-surface)); transition: background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease; }
.density-option:hover { background: rgba(var(--v-theme-primary), 0.04); }
.density-option--selected { border-color: rgb(var(--v-theme-primary)); background: rgba(var(--v-theme-primary), 0.06); box-shadow: 0 0 0 1px rgb(var(--v-theme-primary)); }
.density-option:has(input:focus-visible) { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: 3px; }
.density-option input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.density-label { display: flex; justify-content: space-between; align-items: center; gap: 3px; font-size: 12px; font-weight: 600; }
.density-description { font-size: 10px; color: rgba(var(--v-theme-on-surface), 0.7); white-space: nowrap; }
.density-miniature { --mini-row: 7px; display: flex; flex-direction: column; gap: 3px; height: 46px; overflow: hidden; margin-bottom: 8px; }
.density-miniature[data-density="compact"] { --mini-row: 5px; }.density-miniature[data-density="comfortable"] { --mini-row: 11px; }
.density-miniature > span { display: grid; grid-template-columns: 1.8fr repeat(3, 1fr); gap: 3px; flex: 0 0 var(--mini-row); }
.density-miniature i { border-radius: 2px; background: rgba(var(--v-theme-primary), 0.2); transition: background-color 160ms ease; }
.density-option--selected .density-miniature i { background: rgba(var(--v-theme-primary), 0.45); }
/* Style cards share the density card chrome; only the miniature tells the two renderings apart. */
/* One row, one column per style: a wrapped third option read as a separate group rather than
   as the third of three. `minmax(0, 1fr)` keeps the columns even whatever the label length. */
.style-options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.style-miniature { display: flex; flex-direction: column; gap: 3px; height: 46px; overflow: hidden; margin-bottom: 8px; }
.style-miniature > span { display: grid; grid-template-columns: 1.8fr repeat(3, 1fr); gap: 3px; flex: 0 0 7px; }
.style-miniature i { border-radius: 2px; background: rgba(var(--v-theme-primary), 0.2); transition: background-color 160ms ease; }
.density-option--selected .style-miniature i { background: rgba(var(--v-theme-primary), 0.45); }
/* Classique: the sheet's pill-shaped toggles on a plain surface. */
.style-miniature[data-style="classic"] i { border-radius: 999px; }
/* Grille: square cells under a tinted header row, rows separated by a rule. */
.style-miniature[data-style="grid"] i { border-radius: 1px; }
.style-miniature[data-style="grid"] > span:first-child i { background: rgba(var(--v-theme-primary), 0.35); }
.density-option--selected .style-miniature[data-style="grid"] > span:first-child i { background: rgba(var(--v-theme-primary), 0.6); }
.style-miniature[data-style="grid"] > span + span { flex-basis: 10px; padding-top: 2px; border-top: 1px solid rgba(var(--v-theme-primary), 0.14); }
/* Mur: cells all the same size — no wide name column — gathered into three blocks, each a
   different step of the one hue. That is what the wall is; the shared four-bars-per-row
   miniature the other two use would have shown a table, which is precisely what it is not. */
.style-miniature[data-style="wall"] > span { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 2px; flex-basis: 9px; }
.style-miniature[data-style="wall"] { gap: 2px; }
.style-miniature[data-style="wall"] i { border-radius: 2px; }
.style-miniature[data-style="wall"] > span:nth-child(-n+2) i:nth-child(-n+2) { background: rgba(var(--v-theme-primary), 0.45); }
.style-miniature[data-style="wall"] > span:nth-child(-n+2) i:nth-child(n+3) { background: rgba(var(--v-theme-primary), 0.16); }
.style-miniature[data-style="wall"] > span:nth-child(n+3) i { background: rgba(var(--v-theme-primary), 0.3); }
.density-option--selected .style-miniature[data-style="wall"] > span:nth-child(-n+2) i:nth-child(-n+2) { background: rgba(var(--v-theme-primary), 0.75); }
.density-option--selected .style-miniature[data-style="wall"] > span:nth-child(-n+2) i:nth-child(n+3) { background: rgba(var(--v-theme-primary), 0.3); }
.density-option--selected .style-miniature[data-style="wall"] > span:nth-child(n+3) i { background: rgba(var(--v-theme-primary), 0.52); }
.appearance-select { max-width: 220px; flex: 0 0 auto; }
.appearance-setting-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; }
.appearance-setting-row p, .appearance-note { font-size: 11px; color: rgba(var(--v-theme-on-surface), 0.7); }
.appearance-setting-row :deep(.v-btn) { min-width: 38px; padding: 0 10px; text-transform: none; }
.sample-small { font-size: 11px; }.sample-normal { font-size: 14px; }.sample-large { font-size: 18px; }
/* The pull-up is calibrated for a note that follows an `.appearance-setting-row`, which carries
   the gap of the settings column. A note INSIDE a fieldset has no such gap to eat into — it
   would ride up under the option cards. */
.appearance-note { margin-top: -10px; }
.appearance-note--under { margin-top: 8px; }
.appearance-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px; border-top: 1px solid rgba(var(--v-theme-primary), 0.12); padding-top: 12px; }
.appearance-footer > span { font-size: 10px; color: rgba(var(--v-theme-on-surface), 0.7); }
@media (max-width: 599px) {
  .density-option { padding: 8px; }.density-label { font-size: 11px; }.density-description { font-size: 9px; }
  .appearance-settings { gap: 16px; }
}
@media (prefers-reduced-motion: reduce) {
  .appearance-settings *, .appearance-settings *::before, .appearance-settings *::after { transition: none !important; animation: none !important; }
}
</style>
